<template>
    <div :data-note-id="note.id">
        <div class="relative">
            <div
                data-testid="note-bordered-thread-gap-after"
                class="absolute top-full -mt-6 h-12 left-14 border-l-2 border-gray-300 pointer-events-none z-0 | hidden sm:block"
                v-if="showBorderedThreadGapAfter"
            />

            <div
                data-testid="note-card"
                class="relative z-10 p-6 bg-white | group transition hover:bg-gray-100"
                :class="[
                    noteCardClass,
                    {
                        'sm:pl-21': depth >= 1 && !bordered,
                        'sm:ml-8': depth >= 1 && bordered,
                        'border rounded-lg': bordered,
                        'border-gray-200': bordered && !showInlineEditor,
                        'border-blue-500': bordered && showInlineEditor,
                        'ring-2 ring-red-500 ring-offset-2': isHighlighted,
                    },
                ]"
            >
                <div class="flex flex-col gap-5" v-if="showInlineEditor">
                    <div
                        data-testid="note-edit-card-header"
                        class="flex items-center justify-between gap-3"
                    >
                        <div class="flex items-center gap-3 min-w-0">
                            <note-avatar class="hidden sm:inline-flex" :user="note.user" />

                            <span class="font-semibold truncate" v-text="ownerUsername" />

                            <activix-tooltip
                                :content="$t('clientCard.privateNote')"
                                v-if="note.private"
                            >
                                <icon name="regular/view-off" />
                            </activix-tooltip>
                        </div>

                        <div class="shrink-0 flex items-center gap-3 text-grey-600">
                            <div
                                data-testid="note-edit-card-actions"
                                class="flex items-center"
                                v-if="showHoverActions"
                            >
                                <slot name="hover-actions" :note="note" />
                            </div>

                            <activix-tooltip :content="noteDateTime">
                                <span
                                    class="text-sm"
                                    data-testid="note-edit-card-date"
                                    v-text="showTime ? displayedDate : noteDate"
                                />
                            </activix-tooltip>
                        </div>
                    </div>

                    <div class="w-full" data-testid="note-inline-editor">
                        <slot name="inline-editor" :note="note" :root-note-id="rootNoteId" />
                    </div>
                </div>

                <div class="flex items-start gap-3 relative" data-testid="note-card-body" v-else>
                    <!-- ANCHOR LINE (avatar-column connector; borderless tree only) -->
                    <div
                        class="absolute top-0 left-0 h-full mt-12 pt-px z-[5] | hidden sm:block"
                        v-if="showReplyAnchor && !bordered"
                    >
                        <div class="h-full w-full ml-6 border-l-2 border-gray-300" />
                    </div>

                    <!-- AVATAR -->
                    <note-avatar class="hidden sm:inline-flex" :user="note.user" />

                    <!-- INFO -->
                    <div
                        data-testid="note-info-row"
                        class="flex-1 min-w-0 flex flex-col justify-between items-stretch gap-3 | sm:flex-row"
                    >
                        <div class="space-y-1.5 w-full min-w-0">
                            <div class="flex justify-between items-center gap-3">
                                <div class="flex items-center gap-3">
                                    <span class="font-semibold" v-text="ownerUsername" />

                                    <activix-tooltip
                                        :content="$t('clientCard.privateNote')"
                                        v-if="note.private"
                                    >
                                        <icon name="regular/view-off" />
                                    </activix-tooltip>

                                    <icon
                                        name="regular/microphone"
                                        v-else-if="note.video_id && !shouldDisplayAudio"
                                    />

                                    <note-replying-to-widget :depth="depth" :note="note" />

                                    <span class="dot-flashing" v-if="isReplying" />

                                    <span
                                        class="px-3 py-0.5 rounded-full text-sm text-white bg-blue-500 | animate-pulse"
                                        v-text="$t('clientCard.boxes.notes.editingThisNote')"
                                        v-else-if="isEditing"
                                    />
                                </div>

                                <div class="text-right text-grey-600 | sm:hidden">
                                    <activix-tooltip :content="noteDateTime">
                                        <span
                                            class="text-sm"
                                            v-text="showTime ? displayedDate : noteDate"
                                        />
                                    </activix-tooltip>
                                    <div
                                        class="text-grey-600 text-xs"
                                        v-text="noteEditedOn"
                                        v-if="note.content || (!note.file_url && !note.video_id)"
                                    />
                                </div>
                            </div>

                            <div
                                class="group relative flex flex-col justify-center items-start gap-1.5"
                                data-testid="note-body"
                            >
                                <div
                                    class="w-full min-w-0 whitespace-pre-line break-words"
                                    v-if="note.content || (!note.file_url && !note.video_id)"
                                >
                                    <span
                                        class="!box-decoration-clone rounded !text-gray-200 !bg-gray-200 select-none"
                                        data-testid="note-masked-content"
                                        v-text="displayContent"
                                        v-if="hideSensitiveInfoMode"
                                    />
                                    <mention-text
                                        :content="displayContent"
                                        v-else-if="useMentionText"
                                    />
                                    <span v-html="formattedContent" v-else />
                                </div>
                                <div
                                    class="py-2 w-full"
                                    :class="{ 'h-20': shouldDisplayAudio }"
                                    v-if="note.video_id"
                                >
                                    <audio-player
                                        :downloadable="true"
                                        :file="audioFile"
                                        :resolve-url="resolveAudioUrl"
                                        placeholder
                                        v-if="shouldDisplayAudio"
                                    />
                                </div>
                                <div class="mt-1" v-if="note.file_url">
                                    <a
                                        class="flex items-center p-1.5 gap-1.5 rounded shadow bg-white"
                                        href="#"
                                        target="_blank"
                                        @mouseenter="prefetchAttachmentUrl"
                                        @focus="prefetchAttachmentUrl"
                                        @click.prevent="openAttachment"
                                    >
                                        <signed-img
                                            kind="commentAttachment"
                                            :params="[note.id]"
                                            :class="{
                                                'blur-sm': hideSensitiveInfoMode,
                                            }"
                                            :alt="attachmentFileName"
                                            class="object-contain max-h-48 border border-gray-300 rounded-sm"
                                            v-if="attachmentIsImage"
                                        />

                                        <template v-else>
                                            <icon
                                                class="text-xl text-gray-700"
                                                name="regular/common-file-text"
                                            />

                                            <span v-text="attachmentFileName" />
                                        </template>
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div
                            data-testid="note-meta-column"
                            class="shrink-0 flex flex-row justify-between items-end gap-3 text-right text-grey-600 | sm:flex-col sm:justify-between"
                        >
                            <div class="flex flex-col gap-1.5 ml-3">
                                <div class="flex items-start gap-2" data-testid="note-date-row">
                                    <div class="flex items-center" v-if="showHoverActions">
                                        <slot name="hover-actions" :note="note" />
                                    </div>

                                    <div class="hidden sm:block">
                                        <activix-tooltip :content="noteDateTime">
                                            <span
                                                class="text-sm"
                                                data-testid="note-date"
                                                v-text="showTime ? displayedDate : noteDate"
                                            />
                                        </activix-tooltip>
                                        <div
                                            class="text-grey-600 text-xs"
                                            data-testid="note-edited-on"
                                            v-text="noteEditedOn"
                                            v-if="
                                                note.content || (!note.file_url && !note.video_id)
                                            "
                                        />
                                    </div>
                                </div>

                                <activix-tooltip
                                    placement="bottom"
                                    :content="alertedUsersTooltip"
                                    v-if="alertedUsers.length"
                                >
                                    <div class="self-end flex -space-x-5">
                                        <figure
                                            class="p-1 rounded-full shadow bg-white"
                                            :key="index"
                                            v-for="(alertedUser, index) in alertedUsers"
                                        >
                                            <activix-avatar
                                                class="rounded-full text-gray-700 bg-gray-200"
                                                :size="27"
                                                :username="alertedUser.fullName"
                                                :src="
                                                    alertedUser.hasProfilePicture()
                                                        ? alertedUser.profile_picture
                                                        : ''
                                                "
                                            />
                                        </figure>
                                    </div>
                                </activix-tooltip>
                            </div>

                            <!-- ACTION BUTTONS -->
                            <div
                                class="flex justify-center items-center gap-1.5 | transition group-hover:opacity-100 | sm:opacity-0"
                                v-if="!readonly"
                            >
                                <activix-tooltip :content="$t('clientCard.reply')">
                                    <activix-button
                                        size="sm"
                                        @click="
                                            $emit('trigger-replying', {
                                                note,
                                                rootNoteId,
                                            })
                                        "
                                    >
                                        <icon name="regular/email-action-reply" />
                                    </activix-button>
                                </activix-tooltip>

                                <activix-tooltip
                                    :content="$t('clientCard.edit')"
                                    v-if="canEditNote"
                                >
                                    <activix-button
                                        size="sm"
                                        @click="
                                            $emit('trigger-editing', {
                                                note,
                                                rootNoteId,
                                            })
                                        "
                                    >
                                        <icon name="regular/pencil-1" />
                                    </activix-button>
                                </activix-tooltip>

                                <activix-tooltip
                                    :content="$t('clientCard.delete')"
                                    v-if="canEditNote"
                                >
                                    <activix-button
                                        :loading="$wait.is(`deleting.note[${note.id}]`)"
                                        size="sm"
                                        @click="
                                            $emit('trigger-deleting', {
                                                note,
                                                rootNoteId,
                                            })
                                        "
                                    >
                                        <icon name="regular/trash-1" />
                                    </activix-button>
                                </activix-tooltip>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="relative" v-if="hasChildren || showInlineReply">
            <!-- ANGLED ANCHOR LINE: borderless tree aligns the elbow to the avatar column. -->
            <div
                class="absolute top-0 left-0 h-6 w-9 mt-6 ml-12 border-l-2 border-b-2 border-gray-300 rounded-bl-lg z-[5] | hidden sm:block"
                v-if="depth === 0 && !bordered"
            />

            <!-- Bordered tree: connect box-to-box through the left indent gutter. -->
            <div
                data-testid="note-bordered-thread-elbow"
                class="absolute top-0 left-4 h-7 w-4 -mt-3 border-l-2 border-b-2 border-gray-300 rounded-bl-lg z-[5] | hidden sm:block"
                v-if="showBorderedThreadElbow"
            />

            <note-list
                :context-note-data="contextNoteData"
                :depth="depth + 1"
                :highlighted-note-id="highlightedNoteId"
                :lead="lead"
                :note-type-filter="noteTypeFilter"
                :notes="note.children"
                :parent-note="note"
                :readonly="readonly"
                :root-note-id="rootNoteId"
                :users="users"
                :variant="variant"
                @trigger-deleting="$emit('trigger-deleting', $event)"
                @trigger-editing="$emit('trigger-editing', $event)"
                @trigger-replying="$emit('trigger-replying', $event)"
                v-if="hasChildren"
            >
                <template #hover-actions="slotProps">
                    <slot name="hover-actions" v-bind="slotProps" />
                </template>
                <template #inline-editor="slotProps">
                    <slot name="inline-editor" v-bind="slotProps" />
                </template>
                <template #inline-reply="slotProps">
                    <slot name="inline-reply" v-bind="slotProps" />
                </template>
            </note-list>

            <div
                data-testid="note-inline-reply"
                class="relative mt-3"
                :class="{
                    'sm:ml-8': bordered,
                    'sm:pl-21': !bordered,
                }"
                v-if="showInlineReply"
            >
                <div
                    data-testid="note-bordered-thread-gap-before-reply"
                    class="absolute -top-6 h-12 left-6 border-l-2 border-gray-300 pointer-events-none z-0 | hidden sm:block"
                    v-if="showBorderedInlineReplyGap"
                />

                <div class="relative z-10">
                    <slot
                        name="inline-reply"
                        :depth="depth + 1"
                        :note="note"
                        :root-note-id="rootNoteId"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { defineAsyncComponent } from 'vue';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { useContextStore } from '@/store/modules/context/store.ts';
    import { ActivixButton } from '@/components/activix-ui/index.ts';

    import autolinker from 'autolinker';

    import AudioPlayer from '@/components/audio/AudioPlayer.vue';
    import NoteAvatar from '@/components/container/lead/notes/NoteAvatar.vue';
    import NoteReplyingToWidget from '@/components/container/lead/notes/NoteReplyingToWidget.vue';
    import { NoteListVariant } from '@/components/container/lead/notes/NoteListVariant.ts';
    import MentionText from '@/components/MentionText/MentionText.vue';
    import SignedImg from '@/plugins/signedUrl/SignedImg.vue';

    import Comment from '@/entities/Comment.ts';
    import CommentType from '@/entities/CommentType.js';
    import Lead from '@/entities/Lead.js';
    import NoteAction from '@/entities/NoteAction.js';
    import User from '@/entities/User.js';
    import ActivixDate from '@/value-objects/ActivixDate.js';

    export default {
        components: {
            ActivixButton,
            AudioPlayer,
            NoteAvatar,
            // Recursive (NoteList ⇄ NoteListItem); the Vue 3 async wrapper breaks the import
            // cycle. A bare factory renders as a Promise under a non-compat Vue instance.
            NoteList: defineAsyncComponent(() => import('./NoteList.vue')),
            NoteReplyingToWidget,
            MentionText,
            SignedImg,
        },

        emits: ['trigger-replying', 'trigger-editing', 'trigger-deleting'],

        props: {
            contextNoteData: {
                type: Object,
                required: true,
            },
            depth: {
                type: Number,
                required: true,
            },
            lastInGroup: {
                type: Boolean,
                default: false,
            },
            highlightedNoteId: {
                type: Number,
                default: null,
            },
            note: {
                type: Comment,
                required: true,
            },
            noteTypeFilter: {
                type: Array,
                default: () => [],
            },
            readonly: {
                type: Boolean,
                default: false,
            },
            rootNoteId: {
                type: Number,
                required: true,
            },
            users: {
                type: Array,
                required: true,
            },
            lead: {
                type: Lead,
                required: true,
            },
            variant: {
                type: String,
                default: NoteListVariant.DEFAULT,
                validator: (value) => Object.values(NoteListVariant).includes(value),
            },
        },

        computed: {
            bordered() {
                return this.variant === NoteListVariant.CUSTOMER_PROFILE;
            },

            noteCardClass() {
                return this.bordered ? 'mt-3' : '';
            },

            showHoverActions() {
                return this.bordered;
            },

            showTime() {
                return this.bordered;
            },

            useMentionText() {
                return this.bordered;
            },

            showInlineEditor() {
                return this.bordered && this.isEditing;
            },

            showInlineReply() {
                return this.bordered && this.isReplying;
            },

            showBorderedThreadGapAfter() {
                return (
                    this.bordered &&
                    this.depth >= 1 &&
                    (!this.lastInGroup || this.hasChildren || this.showInlineReply)
                );
            },

            showBorderedInlineReplyGap() {
                return (
                    this.bordered && this.showInlineReply && (this.depth >= 1 || this.hasChildren)
                );
            },

            showBorderedThreadElbow() {
                return (
                    this.bordered && this.depth === 0 && (this.hasChildren || this.showInlineReply)
                );
            },

            hideSensitiveInfoMode() {
                return useContextStore().hideSensitiveInfoMode;
            },

            displayedDate() {
                return this.formatNoteDate(
                    as_locale(this.note.created_at, 'created_at'),
                    this.showTime
                );
            },

            authUser() {
                return useUserStore().authUser;
            },

            alertedUsers() {
                return this.note.alert_users
                    .map((item) => {
                        const user = this.users.find((user) => user.id === item);

                        return user ? new User(user) : null;
                    })
                    .filter((item) => item);
            },

            alertedUsersTooltip() {
                return this.alertedUsers.map((user) => user.fullName).join(', ');
            },

            // Strip the query string before splitting so signed URLs
            // (e.g. ?Expires=...&Signature=...&Key-Pair-Id=...) don't leak
            // into the displayed file name.
            attachmentPath() {
                if (!this.note.file_url) {
                    return '';
                }

                return this.note.file_url.split('?')[0];
            },

            attachmentFileName() {
                if (!this.attachmentPath) {
                    return '';
                }

                return this.attachmentPath.split('/').pop();
            },

            attachmentIsImage() {
                if (!this.attachmentPath) {
                    return false;
                }

                const path = this.attachmentPath.toLowerCase();

                return ['.png', '.jpg', '.jpeg'].some((type) => path.endsWith(type));
            },

            audioFile() {
                return this.note.video;
            },

            canEditNote() {
                return (
                    this.authUser.isAdmin() ||
                    (this.createdByAuthUser && !this.note.request_user_id)
                );
            },

            createdByAuthUser() {
                return this.authUser.isSameUser(this.note.user_id);
            },

            displayContent() {
                let content = this.note.content;

                if (this.note.request_user_id) {
                    content = `${this.$t('clientCard.assignationRequest')} : ${content}`;
                }

                return content;
            },

            formattedContent() {
                return autolinker.link(this.displayContent, {
                    sanitizeHtml: true,
                });
            },

            hasChildren() {
                return this.note.children?.length > 0;
            },

            isContextNote() {
                return this.contextNoteData.note?.id === this.note.id;
            },

            isHighlighted() {
                return this.highlightedNoteId === this.note.id;
            },

            isEditing() {
                return this.isContextNote && this.contextNoteData.action === NoteAction.EDIT;
            },

            isReplying() {
                return this.isContextNote && this.contextNoteData.action === NoteAction.REPLY;
            },

            noteDate() {
                const createdAt = as_locale(this.note.created_at, 'created_at');
                if (createdAt.isSameDay(now())) {
                    const date = createdAt.fromNow(false, this.$i18n.getLocale());

                    return date[0].toUpperCase() + date.slice(1);
                }

                return this.formatNoteDate(createdAt);
            },

            noteDateTime() {
                const createdAt = as_locale(this.note.created_at, 'created_at');

                if (createdAt.isSameDay(now())) {
                    return '';
                }

                return createdAt.timeHumanShort(this.$i18n.getLocale());
            },

            noteEditedOn() {
                const createdAt = as_locale(this.note.created_at, 'created_at');
                const updatedAt = as_locale(this.note.updated_at, 'updated_at');

                if (updatedAt.isSame(createdAt)) {
                    return '';
                }

                if (updatedAt.isSameDay(now())) {
                    return `${this.$t('clientCard.edited')} ${updatedAt.fromNow(
                        false,
                        this.$i18n.getLocale()
                    )}`;
                }

                const updatedDate = this.formatNoteDate(updatedAt);

                return `${this.$t('clientCard.edited')} ${updatedDate}`;
            },

            ownerUsername() {
                return this.note.user.fullName || 'Activix';
            },

            shouldDisplayAudio() {
                return (
                    !this.noteTypeFilter.length || this.noteTypeFilter.includes(CommentType.AUDIO)
                );
            },

            showReplyAnchor() {
                if (this.depth === 0) {
                    return this.hasChildren;
                }

                return !this.lastInGroup;
            },
        },

        methods: {
            formatNoteDate(date, withTime = false) {
                return new ActivixDate(date.toDate()).toHumanShort(
                    withTime,
                    this.$i18n.getLocale()
                );
            },

            // Audio URL is resolved via the global $signedUrl plugin so the
            // TTL starts at click time, not page load.
            resolveAudioUrl() {
                return this.$signedUrl.resolve('audioNote', this.note.id);
            },

            // Pre-fetch the attachment URL on hover/focus so the link works
            // on the first click (otherwise href="#" is followed before
            // we have time to redirect).
            prefetchAttachmentUrl() {
                this.$signedUrl.resolve('commentAttachment', this.note.id).catch(() => null);
            },

            async openAttachment() {
                const url = await this.$signedUrl
                    .resolve('commentAttachment', this.note.id)
                    .catch(() => null);
                if (url) {
                    window.open(url, '_blank', 'noopener');
                }
            },
        },
    };
</script>
